import { useState } from "react";

const METRIC_CONFIG = {
  visibility: {
    dataKey: "baseResponses",
    fallbackKey: "baseResponses",
    showResponse: true,
    showPrompt: true,
    titlePrefix: "Rank - Base Responses"
  },
  sentiment: {
    dataKey: "completeBaseRes.sentimentQuestionBaseRes",
    showResponse: false,
    showPrompt: true,
    titlePrefix: "Sentiment Question - Base Prompt"
  },
  "sentiment-analysis": {
    dataKey: "completeBaseRes.sentimentAnalysisBasePrompt",
    showResponse: false,
    showPrompt: true,
    isDirectString: true,
    titlePrefix: "Sentiment Analysis - Base Prompt"
  },
  "sentiment-drivers-positive": {
    dataKey: "completeBaseRes.baseSentimentDriverRes",
    showResponse: false,
    showPrompt: true,
    isDirectString: true,
    arrayIndex: 0,
    promptField: "positiveSentimentDriversPrompt",
    titlePrefix: "Positive Sentiment Drivers - Base Prompt"
  },
  "sentiment-drivers-negative": {
    dataKey: "completeBaseRes.baseSentimentDriverRes",
    showResponse: false,
    showPrompt: true,
    isDirectString: true,
    arrayIndex: 1,
    promptField: "negativeSentimentDriversPrompt",
    titlePrefix: "Negative Sentiment Drivers - Base Prompt"
  }
};

export default function useBaseResponsesModal(data, metricType = "visibility") {
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [infoModalContent, setInfoModalContent] = useState("");
  const [infoModalTitle, setInfoModalTitle] = useState("");
  const productMappingAttribute = data?.productMappingAttribute;
  const hasSections =
    productMappingAttribute &&
    Object.keys(productMappingAttribute).length > 1;

  const getNestedProperty = (obj, path) => {
    return path.split('.').reduce((acc, part) => acc?.[part], obj);
  };

  // Get base responses based on metric type using configuration
  const getBaseResponsesByMetric = (metric) => {
    const config = METRIC_CONFIG[metric] || {};

    if (config.isDirectString) {
      const baseData = getNestedProperty(data, config.dataKey);

      if (config.arrayIndex !== undefined && config.promptField) {
        return baseData?.[config.arrayIndex]?.[config.promptField] || "";
      }

      return baseData || "";
    }

    let responses = getNestedProperty(data, config.dataKey);

    if ((!responses || responses.length === 0) && config.fallbackKey) {
      responses = getNestedProperty(data, config.fallbackKey);
    }

    return responses || [];
  };

  //Filter items based on matching criteria (for sentiment questions)
  const filterItemsByMatch = (items, matchCriteria) => {
    if (!matchCriteria) return items;

    const { field = "basePrompt", value } = matchCriteria;

    const matchingItem = items.find(item =>
      item?.[field] && item[field].includes(value)
    );

    return matchingItem ? [matchingItem] : [];
  };

  // Format content based on metric configuration and data structure
  const formatContentByMetric = (items, metric, options = {}) => {
    const config = METRIC_CONFIG[metric] || {};
    const { matchCriteria = null } = options;

    if (config.isDirectString && typeof items === 'string') {
      return `### Base Prompt\n\n${items}`;
    }

    const itemsArray = Array.isArray(items) ? items : [items];

    // Filter items if match criteria provided 
    const filteredItems = filterItemsByMatch(itemsArray, matchCriteria);

    if (filteredItems.length === 0) {
      return `### ${config.titlePrefix}\n\nNo matching data found.`;
    }

    const hasStructuredData = filteredItems.every(
      (item) => item && typeof item === 'object' && item.basePrompt
    );

    if (!hasStructuredData) {
      return filteredItems
        .map((item, idx) => `### Response ${idx + 1}\n\n${item}`)
        .join("\n\n---\n\n");
    }

    const firstPrompt = filteredItems[0].basePrompt;
    const allSamePrompt = filteredItems.every(
      (item) => item.basePrompt === firstPrompt
    );

    if (allSamePrompt) {
      return formatSinglePromptContent(filteredItems, firstPrompt, config);
    } else {
      return formatMultiplePromptsContent(filteredItems, config);
    }
  };

  // Format content when all items share the same prompt
  const formatSinglePromptContent = (items, prompt, config) => {
    let content = "";

    if (config.showPrompt) {
      content = `### Base Prompt\n\n${prompt}`;
    }

    if (config.showResponse && items[0]?.baseResponse) {
      const responsesContent = items
        .map((item, idx) => `**Response ${idx + 1}:**\n\n${item.baseResponse}`)
        .join("\n\n---\n\n");

      content += content ? `\n\n---\n\n### Responses\n\n${responsesContent}`
        : `### Responses\n\n${responsesContent}`;
    }

    return content;
  };

  // Format content when items have different prompts
  const formatMultiplePromptsContent = (items, config) => {
    const groupedByPrompt = items.reduce((acc, item) => {
      const prompt = item.basePrompt;
      if (!acc[prompt]) {
        acc[prompt] = [];
      }
      if (item.baseResponse) {
        acc[prompt].push(item.baseResponse);
      }
      return acc;
    }, {});

    return Object.entries(groupedByPrompt)
      .map(([prompt, responses], queryIdx) => {
        let section = `### Query ${queryIdx + 1}`;

        if (config.showPrompt) {
          section += `\n\n**Base Prompt:**\n\n${prompt}`;
        }

        if (config.showResponse && responses.length > 0) {
          const responsesContent = responses
            .map((response, idx) => `**Response ${idx + 1}:**\n\n${response}`)
            .join("\n\n---\n\n");

          section += `\n\n---\n\n**Responses:**\n\n${responsesContent}`;
        }

        return section;
      })
      .join("\n\n---\n\n");
  };

  // Generic handler for info clicks
  function handleInfoClick(sectionKey, indices, options = {}) {
    const { metric = metricType } = options;
    const baseResponses = getBaseResponsesByMetric(metric);

    if (typeof baseResponses === 'string') {
      const content = formatContentByMetric(baseResponses, metric);
      const config = METRIC_CONFIG[metric] || {};
      setInfoModalContent(content);
      setInfoModalOpen(true);
      return;
    }

    const selectedItems = indices
      .filter((i) => baseResponses[i])
      .map((i) => baseResponses[i]);

    const content = formatContentByMetric(selectedItems, metric);
    const config = METRIC_CONFIG[metric] || {};

    setInfoModalContent(content);
    setInfoModalTitle(config.titlePrefix);
    setInfoModalOpen(true);
  }

  // Handle all base responses click
  function handleAllBaseResponsesClick(options = {}) {
    const { metric = metricType } = options;
    const baseResponses = getBaseResponsesByMetric(metric);

    if (!baseResponses || (Array.isArray(baseResponses) && baseResponses.length === 0)) {
      return;
    }

    const validItems = Array.isArray(baseResponses)
      ? baseResponses.filter(Boolean)
      : baseResponses;

    const content = formatContentByMetric(validItems, metric);
    const config = METRIC_CONFIG[metric] || METRIC_CONFIG.visibility;

    setInfoModalContent(content);
    setInfoModalTitle(config.titlePrefix || "Base Responses");
    setInfoModalOpen(true);
  }

  //Handle sentiment question specific info click
  function handleSentimentQuestionClick(questionText) {
    const baseResponses = getBaseResponsesByMetric("sentiment");
    const content = formatContentByMetric(baseResponses, "sentiment", {
      matchCriteria: { field: "basePrompt", value: questionText }
    });

    const config = METRIC_CONFIG.sentiment;
    setInfoModalContent(content);
    setInfoModalTitle(config.titlePrefix);
    setInfoModalOpen(true);
  }

  // Handle sentiment analysis (explanation) info click
  function handleSentimentAnalysisClick() {
    const analysisPrompt = getBaseResponsesByMetric("sentiment-analysis");
    const content = formatContentByMetric(analysisPrompt, "sentiment-analysis");

    const config = METRIC_CONFIG["sentiment-analysis"];
    setInfoModalContent(content);
    setInfoModalTitle(config.titlePrefix);
    setInfoModalOpen(true);
  }

  // Handle positive sentiment drivers info click
  function handlePositiveSentimentDriversClick() {
    const driversPrompt = getBaseResponsesByMetric("sentiment-drivers-positive");
    const content = formatContentByMetric(driversPrompt, "sentiment-drivers-positive");

    const config = METRIC_CONFIG["sentiment-drivers-positive"];
    setInfoModalContent(content);
    setInfoModalTitle(config.titlePrefix);
    setInfoModalOpen(true);
  }

  // Handle negative sentiment drivers info click
  function handleNegativeSentimentDriversClick() {
    const driversPrompt = getBaseResponsesByMetric("sentiment-drivers-negative");
    const content = formatContentByMetric(driversPrompt, "sentiment-drivers-negative");

    const config = METRIC_CONFIG["sentiment-drivers-negative"];
    setInfoModalContent(content);
    setInfoModalTitle(config.titlePrefix);
    setInfoModalOpen(true);
  }


  function closeInfoModal() {
    setInfoModalOpen(false);
  }

  return {
    infoModalOpen,
    infoModalTitle,
    infoModalContent,
    hasSections,
    productMappingAttribute,
    closeInfoModal,
    handleInfoClick,
    handleAllBaseResponsesClick,
    handleSentimentQuestionClick,
    handleSentimentAnalysisClick,
    handlePositiveSentimentDriversClick,
    handleNegativeSentimentDriversClick,
  };
}